import React, { useState, useRef } from "react";
import { Upload, Link as LinkIcon, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { adminUploadMedia, mediaUrl } from "../../lib/api";

/**
 * Compact image picker for admin forms.
 * Value is a URL string (either a full http… URL or a /api/media/… path).
 */
const ImagePicker = ({ value, onChange, label = "Image", testId }) => {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState(value || "");
  const inputRef = useRef(null);

  React.useEffect(() => {
    setUrl(value || "");
  }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That doesn't look like an image");
      return;
    }
    setUploading(true);
    try {
      const r = await adminUploadMedia(file, "");
      onChange(r.url); // /api/media/<id>
      setUrl(r.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const commitUrl = () => {
    onChange(url);
  };

  const preview = mediaUrl(value);

  return (
    <div data-testid={testId}>
      {label && <label className="text-xs text-muted-foreground block mb-1">{label}</label>}
      <div className="flex items-start gap-4">
        <div className="w-28 h-20 border border-border bg-muted/40 overflow-hidden flex items-center justify-center shrink-0">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
          ) : (
            <ImageIcon size={20} className="text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="text-xs px-3 py-2 border border-border hover:bg-muted/40 inline-flex items-center gap-1"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Upload
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <LinkIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                className="input-field pl-7 text-xs"
                placeholder="…or paste image URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={commitUrl}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitUrl();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePicker;
